import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, RefreshCw, CheckCircle2, Loader2, Download,
  ChevronLeft, ChevronRight, Search, Wand2, History, AlertTriangle,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { extractBookingWindow, type MetaItem } from "../../supabase/functions/_shared/wpBookingParse";

type Failure = {
  id: string;
  wp_order_id: string;
  wp_line_item_id: string;
  product_name: string | null;
  billing_email: string | null;
  order_status: string | null;
  line_total: number | null;
  raw_meta: MetaItem[];
  reason: string | null;
  status: string;
  resolved_booking_id: string | null;
  created_at: string;
};

type Studio = { id: string; name: string };

type EventRow = {
  id: string;
  failure_id: string;
  event_type: string;
  actor_id: string | null;
  details: any;
  created_at: string;
};

const PAGE_SIZE = 25;
const REASONS = ["no_start", "no_end", "no_meta", "other"];

const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const csvEscape = (v: unknown) => {
  if (v == null) return "";
  const s = typeof v === "string" ? v : JSON.stringify(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export default function AdminWpImportFailures() {
  const [rows, setRows] = useState<Failure[]>([]);
  const [total, setTotal] = useState(0);
  const [studios, setStudios] = useState<Studio[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<
    Record<string, { start: string; end: string; studio_id: string }>
  >({});

  // Filters
  const [showResolved, setShowResolved] = useState(false);
  const [q, setQ] = useState(""); // free-text (WP order id / product / email)
  const [reason, setReason] = useState<string>("");
  const [studioFilter, setStudioFilter] = useState<string>("");
  const [page, setPage] = useState(0);

  // Audit events expansion
  const [openEvents, setOpenEvents] = useState<Record<string, EventRow[] | null>>({});

  const studioById = useMemo(
    () => Object.fromEntries(studios.map((s) => [s.id, s.name])),
    [studios],
  );

  const buildQuery = (countOnly = false) => {
    let qry = supabase
      .from("wp_booking_failures" as any)
      .select("*", countOnly ? { count: "exact", head: true } : { count: "exact" });
    if (!showResolved) qry = qry.eq("status", "unresolved");
    if (reason) qry = qry.eq("reason", reason);
    if (q.trim()) {
      const t = q.trim().replace(/[%,]/g, "");
      qry = qry.or(
        `wp_order_id.ilike.%${t}%,product_name.ilike.%${t}%,billing_email.ilike.%${t}%`,
      );
    }
    if (studioFilter) {
      const name = studios.find((s) => s.id === studioFilter)?.name;
      if (name) qry = qry.ilike("product_name", `%${name.replace(/[%,]/g, "")}%`);
    }
    return qry;
  };

  const load = async () => {
    setLoading(true);
    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;
    const [{ data, count, error }, { data: s }] = await Promise.all([
      buildQuery().order("created_at", { ascending: false }).range(from, to),
      studios.length ? Promise.resolve({ data: studios }) : supabase.from("studios").select("id,name").order("name"),
    ]);
    if (error) toast.error(error.message);
    setRows(((data as any) ?? []) as Failure[]);
    setTotal(count ?? 0);
    if (!studios.length && s) setStudios(s as Studio[]);
    setLoading(false);
  };

  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, [showResolved, reason, page, studioFilter]);

  // Reset to page 0 when filters change (except page itself)
  useEffect(() => { setPage(0); }, [showResolved, reason, q, studioFilter]);

  const setDraft = (
    id: string,
    patch: Partial<{ start: string; end: string; studio_id: string }>,
  ) =>
    setDrafts((d) => ({
      ...d,
      [id]: { start: "", end: "", studio_id: "", ...d[id], ...patch },
    }));

  // Server filters already applied (incl. studio via product_name match)
  const visibleRows = rows;

  // Inline validation helpers
  const isValidLocalDt = (v: string) => !!v && !isNaN(new Date(v).getTime());
  const studioMatchesProduct = (row: Failure, studioId: string) => {
    if (!studioId) return true;
    const name = studioById[studioId]?.toLowerCase();
    if (!name) return true;
    return (row.product_name ?? "").toLowerCase().includes(name);
  };
  const draftIssues = (row: Failure, d: { start: string; end: string; studio_id: string }) => {
    const issues: { start?: string; end?: string; studio?: string } = {};
    if (d.start && !isValidLocalDt(d.start)) issues.start = "Invalid date/time format";
    if (d.end && !isValidLocalDt(d.end)) issues.end = "Invalid date/time format";
    if (isValidLocalDt(d.start) && isValidLocalDt(d.end) && new Date(d.end) <= new Date(d.start)) {
      issues.end = "End must be after start";
    }
    if (d.studio_id && !studioMatchesProduct(row, d.studio_id)) {
      issues.studio = `Studio doesn't match product "${row.product_name ?? ""}"`;
    }
    return issues;
  };
  const canSave = (row: Failure, d: { start: string; end: string; studio_id: string }) => {
    if (!d.start || !d.end || !d.studio_id) return false;
    const i = draftIssues(row, d);
    return !i.start && !i.end && !i.studio;
  };

  async function resolve(row: Failure, override?: { start: string; end: string; studio_id: string }) {
    const d = override ?? drafts[row.id];
    if (!d?.start || !d?.end || !d?.studio_id) {
      toast.error("Start, end and studio are all required");
      return;
    }
    const issues = draftIssues(row, d);
    if (issues.start || issues.end || issues.studio) {
      toast.error(issues.start ?? issues.end ?? issues.studio ?? "Invalid draft");
      return;
    }
    const startISO = new Date(d.start).toISOString();
    const endISO = new Date(d.end).toISOString();
    setBusyId(row.id);
    try {
      const email = (row.billing_email ?? "").toLowerCase();
      const customer = email
        ? (await supabase.from("customers").select("id").eq("email", email).maybeSingle()).data
        : null;
      const paid = ["completed", "processing"].includes(String(row.order_status));
      const total = Number(row.line_total ?? 0);
      const { data: ins, error } = await supabase
        .from("bookings")
        .insert({
          studio_id: d.studio_id,
          customer_id: customer?.id ?? null,
          start_at: startISO,
          end_at: endISO,
          status: (paid ? "confirmed" : "pending") as any,
          payment_status: (paid ? "paid" : "unpaid") as any,
          subtotal: total,
          total,
          amount_paid: paid ? total : 0,
          notes: `Manually resolved from WP order #${row.wp_order_id} (${row.product_name ?? ""})`,
        })
        .select("id")
        .single();
      if (error || !ins) throw new Error(error?.message ?? "insert failed");

      await supabase.from("wp_id_map").insert({
        entity_type: "booking",
        wp_id: `booking:${row.wp_order_id}:${row.wp_line_item_id}`,
        local_id: ins.id,
        metadata: { wp_order_id: row.wp_order_id, wp_line_item_id: row.wp_line_item_id, manual: true },
      });
      await supabase
        .from("wp_booking_failures" as any)
        .update({ status: "resolved", resolved_booking_id: ins.id, resolved_at: new Date().toISOString() })
        .eq("id", row.id);
      toast.success("Booking created");
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function ignore(row: Failure) {
    setBusyId(row.id);
    await supabase
      .from("wp_booking_failures" as any)
      .update({ status: "ignored", resolved_at: new Date().toISOString() })
      .eq("id", row.id);
    setBusyId(null);
    load();
  }

  async function retryParse(row: Failure, autoCreate = false) {
    const res = extractBookingWindow(row.raw_meta ?? []);
    if (!res.startISO || !res.endISO) {
      toast.error(`Re-parse still failed (${res.reason ?? "unknown"})`);
      // Log a retry event with the new reason
      await supabase.from("wp_booking_failure_events" as any).insert({
        failure_id: row.id,
        event_type: "retry_failed",
        details: { reason: res.reason },
      });
      // refresh the open audit log if it's expanded
      if (openEvents[row.id]) await loadEvents(row.id);
      return;
    }
    const start = toLocalInput(res.startISO);
    const end = toLocalInput(res.endISO);
    setDraft(row.id, { start, end });
    await supabase.from("wp_booking_failure_events" as any).insert({
      failure_id: row.id,
      event_type: "retry_succeeded",
      details: { start: res.startISO, end: res.endISO, persons: res.persons },
    });
    if (openEvents[row.id]) await loadEvents(row.id);
    const existing = drafts[row.id];
    const studioId = existing?.studio_id ?? "";
    if (autoCreate && studioId && studioMatchesProduct(row, studioId)) {
      toast.success("Re-parse succeeded — auto-creating booking…");
      await resolve(row, { start, end, studio_id: studioId });
    } else {
      toast.success("Re-parse succeeded — review and click Create booking");
    }
  }

  async function loadEvents(failureId: string) {
    const { data, error } = await supabase
      .from("wp_booking_failure_events" as any)
      .select("*")
      .eq("failure_id", failureId)
      .order("created_at", { ascending: true });
    if (error) {
      toast.error(error.message);
      return;
    }
    setOpenEvents((m) => ({ ...m, [failureId]: (data as any) ?? [] }));
  }

  async function toggleEvents(failureId: string) {
    if (openEvents[failureId]) {
      setOpenEvents((m) => ({ ...m, [failureId]: null }));
    } else {
      await loadEvents(failureId);
    }
  }

  async function exportCsv() {
    setExporting(true);
    try {
      // Pull all matching rows (no pagination) — capped at 5000 for safety
      let qry = supabase.from("wp_booking_failures" as any).select("*").limit(5000);
      qry = qry.eq("status", "unresolved");
      if (reason) qry = qry.eq("reason", reason);
      if (q.trim()) {
        const t = q.trim().replace(/[%,]/g, "");
        qry = qry.or(
          `wp_order_id.ilike.%${t}%,product_name.ilike.%${t}%,billing_email.ilike.%${t}%`,
        );
      }
      if (studioFilter) {
        const name = studios.find((s) => s.id === studioFilter)?.name;
        if (name) qry = qry.ilike("product_name", `%${name.replace(/[%,]/g, "")}%`);
      }
      const { data, error } = await qry.order("created_at", { ascending: false });
      if (error) throw error;
      const all = ((data as any) ?? []) as Failure[];
      const headers = [
        "id", "created_at", "wp_order_id", "wp_line_item_id", "product_name",
        "billing_email", "order_status", "line_total", "reason", "status", "raw_meta",
      ];
      const lines = [headers.join(",")];
      for (const r of all) {
        lines.push(headers.map((h) => csvEscape((r as any)[h])).join(","));
      }
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wp-booking-failures-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setExporting(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-6xl mx-auto px-6 py-10 space-y-6">
        <Link to="/admin/wp-import" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Back to WP Import
        </Link>
        <header className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-extrabold uppercase tracking-tight">Booking Import Failures</h1>
            <p className="text-muted-foreground text-sm max-w-2xl mt-2">
              Line items where the importer couldn't extract a valid start/end window from YITH meta.
              Pick start, end and studio below and the booking is created and linked back.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={showResolved}
                onChange={(e) => setShowResolved(e.target.checked)}
              />
              Show resolved / ignored
            </label>
            <Button variant="outline" onClick={exportCsv} disabled={exporting} className="gap-2">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export CSV
            </Button>
            <Button variant="outline" onClick={load} className="gap-2">
              <RefreshCw className="w-4 h-4" /> Refresh
            </Button>
          </div>
        </header>

        {/* Filters */}
        <Card className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-2">
            <Label className="text-xs">Search (WP order #, product, email)</Label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="e.g. 12345, Studio A, jane@…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Reason</Label>
            <select
              className="w-full h-10 border border-border bg-background px-2"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              <option value="">All</option>
              {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Studio (draft or product match)</Label>
            <select
              className="w-full h-10 border border-border bg-background px-2"
              value={studioFilter}
              onChange={(e) => setStudioFilter(e.target.value)}
            >
              <option value="">All</option>
              {studios.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </Card>

        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {loading ? "Loading…" : `${total.toLocaleString()} total · showing ${visibleRows.length}`}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              disabled={page === 0 || loading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="gap-1"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </Button>
            <span>Page {page + 1} / {totalPages}</span>
            <Button
              variant="outline" size="sm"
              disabled={page + 1 >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
              className="gap-1"
            >
              Next <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {!loading && visibleRows.length === 0 && (
          <Card className="p-8 text-center text-muted-foreground">
            Nothing here — every booking parsed cleanly or no results match your filters.
          </Card>
        )}

        <div className="space-y-4">
          {visibleRows.map((row) => {
            const d = drafts[row.id] ?? { start: "", end: "", studio_id: "" };
            const isResolved = row.status !== "unresolved";
            const events = openEvents[row.id];
            const issues = draftIssues(row, d);
            const saveOk = canSave(row, d);
            return (
              <Card key={row.id} className="p-5 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-semibold">{row.product_name || "(no name)"}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      WP order #{row.wp_order_id} · line {row.wp_line_item_id} ·{" "}
                      {row.billing_email ?? "no email"} · status {row.order_status ?? "?"} · £{Number(row.line_total ?? 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <Badge variant={isResolved ? "default" : "destructive"}>{row.status}</Badge>
                    {row.reason && <Badge variant="outline">{row.reason}</Badge>}
                  </div>
                </div>

                <div className="border border-border bg-muted/30 p-3 max-h-40 overflow-auto text-xs font-mono">
                  {row.raw_meta?.length
                    ? row.raw_meta.map((m, i) => (
                        <div key={i}>
                          <span className="text-muted-foreground">{m.display_key ?? m.key}:</span>{" "}
                          {typeof (m.display_value ?? m.value) === "string"
                            ? (m.display_value ?? m.value)
                            : JSON.stringify(m.display_value ?? m.value)}
                        </div>
                      ))
                    : <span className="text-muted-foreground">(no meta)</span>}
                </div>

                {!isResolved && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                    <div>
                      <Label className="text-xs">Start</Label>
                      <Input
                        type="datetime-local"
                        value={d.start}
                        onChange={(e) => setDraft(row.id, { start: e.target.value })}
                        className={issues.start ? "border-destructive" : ""}
                      />
                      {issues.start && <p className="text-xs text-destructive mt-1">{issues.start}</p>}
                    </div>
                    <div>
                      <Label className="text-xs">End</Label>
                      <Input
                        type="datetime-local"
                        value={d.end}
                        onChange={(e) => setDraft(row.id, { end: e.target.value })}
                        className={issues.end ? "border-destructive" : ""}
                      />
                      {issues.end && <p className="text-xs text-destructive mt-1">{issues.end}</p>}
                    </div>
                    <div>
                      <Label className="text-xs">Studio</Label>
                      <select
                        className={`w-full h-10 border bg-background px-2 ${issues.studio ? "border-destructive" : "border-border"}`}
                        value={d.studio_id}
                        onChange={(e) => setDraft(row.id, { studio_id: e.target.value })}
                      >
                        <option value="">—</option>
                        {studios.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                      {issues.studio && (
                        <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> {issues.studio}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button onClick={() => resolve(row)} disabled={busyId === row.id || !saveOk} className="gap-2">
                        {busyId === row.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        Create booking
                      </Button>
                      <Button variant="outline" onClick={() => retryParse(row)} disabled={busyId === row.id} className="gap-2">
                        <Wand2 className="w-4 h-4" /> Retry parse
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => retryParse(row, true)}
                        disabled={busyId === row.id || !d.studio_id || !!issues.studio}
                        title={!d.studio_id ? "Pick a studio first" : issues.studio ?? "Re-parse and create if valid"}
                        className="gap-2"
                      >
                        <Wand2 className="w-4 h-4" /> Retry & auto-create
                      </Button>
                      <Button variant="outline" onClick={() => ignore(row)} disabled={busyId === row.id}>
                        Ignore
                      </Button>
                    </div>
                  </div>
                )}

                <div>
                  <Button variant="ghost" size="sm" onClick={() => toggleEvents(row.id)} className="gap-2">
                    <History className="w-4 h-4" />
                    {events ? "Hide audit log" : "Show audit log"}
                  </Button>
                  {events && (
                    <div className="mt-2 border border-border bg-muted/20 p-3 text-xs space-y-1">
                      {events.length === 0 && <div className="text-muted-foreground">No events.</div>}
                      {events.map((ev) => (
                        <div key={ev.id} className="flex items-start gap-3">
                          <span className="text-muted-foreground whitespace-nowrap">
                            {new Date(ev.created_at).toLocaleString()}
                          </span>
                          <Badge variant="outline">{ev.event_type}</Badge>
                          <span className="text-muted-foreground">
                            {ev.actor_id ? `by ${ev.actor_id.slice(0, 8)}…` : "system"}
                          </span>
                          {ev.details && Object.keys(ev.details).length > 0 && (
                            <span className="font-mono text-muted-foreground truncate">
                              {JSON.stringify(ev.details)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}